// Radha — Automation (Zoho CRM Blueprint style, lightweight). A tenant turns on
// any of a small set of rules; they run hourly via pg_cron and on demand here.
import React, { useState } from "react";

import {
  useAutomationRules, useAutomationLog, useToggleRule, useRunAutomationsNow,
  RULE_LABEL, RULE_HINT, ALL_RULE_TYPES, type AutomationRule,
} from "../hooks/useAutomation";
import { writeErrorMessage } from "../hooks/useCollectionsWrites";
import { useRole } from "../lib/authSupabase";
import { fmtDateTime } from "../lib/money";

const td: React.CSSProperties = { padding: "9px 8px" };

function RuleCard({ ruleType, existing, canWrite }: { ruleType: string; existing?: AutomationRule; canWrite: boolean }) {
  const toggle = useToggleRule();
  const active = existing?.is_active ?? false;
  return (
    <div className="sanctuary-card" style={{ padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{RULE_LABEL[ruleType as keyof typeof RULE_LABEL]}</div>
        <div style={{ color: "var(--fg-muted)", fontSize: 12.5, marginTop: 3, maxWidth: 440 }}>{RULE_HINT[ruleType as keyof typeof RULE_HINT]}</div>
        {existing?.last_run_at && <div style={{ color: "var(--fg-faint)", fontSize: 11, marginTop: 4 }}>Last run {fmtDateTime(existing.last_run_at)}</div>}
      </div>
      {canWrite && (
        <button
          onClick={() => toggle.mutate({ ruleType: ruleType as AutomationRule["rule_type"], threshold: 21, existing })}
          disabled={toggle.isPending}
          style={{ flexShrink: 0, padding: "5px 12px", fontSize: 12, border: `1px solid ${active ? "var(--status-good)" : "var(--border)"}`, color: active ? "var(--status-good)" : "var(--fg-muted)", background: "none", borderRadius: 7, cursor: "pointer" }}>
          {active ? "On" : "Off"}
        </button>
      )}
    </div>
  );
}

export function AutomationPanel() {
  const { data: rules, isLoading, error } = useAutomationRules();
  const { data: log } = useAutomationLog();
  const { canWrite } = useRole();
  const run = useRunAutomationsNow();
  const [note, setNote] = useState<string | null>(null);

  const byType = new Map((rules ?? []).map((r) => [r.rule_type, r]));
  const anyActive = (rules ?? []).some((r) => r.is_active);

  const runNow = () => {
    setNote(null);
    run.mutate(undefined, {
      onSuccess: (n) => setNote(n > 0 ? `Ran automations — ${n} action${n === 1 ? "" : "s"} taken` : "Ran automations — nothing to do"),
      onError: (e) => setNote(writeErrorMessage(e)),
    });
  };

  if (isLoading) return <p style={{ padding: 24 }}>Loading…</p>;
  if (error) return <p style={{ padding: 24, color: "var(--status-critical)" }}>{(error as Error).message}</p>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "4px 0 18px" }}>
        <p style={{ color: "var(--fg-muted)", fontSize: 13, margin: 0 }}>
          Active rules run automatically every hour. Each is idempotent — running it again the same day does nothing extra.
        </p>
        {canWrite && (
          <button onClick={runNow} disabled={run.isPending || !anyActive}
            style={{ padding: "7px 14px", background: anyActive ? "var(--accent-purple)" : "var(--border)", color: "#fff", border: 0, borderRadius: 8, cursor: anyActive ? "pointer" : "default", fontSize: 13, flexShrink: 0 }}>
            {run.isPending ? "Running…" : "Run now"}
          </button>
        )}
      </div>
      {note && <p style={{ fontSize: 13, color: note.startsWith("Ran") ? "var(--status-good)" : "var(--status-critical)", margin: "0 0 14px" }}>{note}</p>}

      <div className="sanctuary-stagger" style={{ display: "grid", gap: 10, marginBottom: 26 }}>
        {ALL_RULE_TYPES.map((rt) => <RuleCard key={rt} ruleType={rt} existing={byType.get(rt)} canWrite={canWrite} />)}
      </div>

      <div style={{ font: "600 15px Inter", margin: "0 0 8px" }}>Recent activity</div>
      <div className="sanctuary-scroll-x">
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }}>
          <tbody>
            {(log ?? []).map((l) => (
              <tr key={l.id} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={{ ...td, color: "var(--fg-muted)", width: 160, fontSize: 12 }}>{fmtDateTime(l.created_at)}</td>
                <td style={{ ...td, color: "var(--fg-muted)", width: 220, fontSize: 12 }}>{RULE_LABEL[l.rule_type as keyof typeof RULE_LABEL] ?? l.rule_type}</td>
                <td style={td}>{l.summary}</td>
              </tr>
            ))}
            {(log ?? []).length === 0 && (
              <tr><td style={{ ...td, color: "var(--fg-muted)" }}>No automation activity yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
